<template>
  <generic-in-app-screen>
    <b-container class="flex-grow-1 d-flex flex-column">
      <h1 class="my-5">
        <b>{{ $t('current_projects_warnings') }}</b>
      </h1>
      <b-card>
        <b-row>
          <b-col>
            <!-- TODO PL : Add each line to be clickable et to link to the details
             in the brick by brick section ? 
             And maybe replace the table by a histogram of the first bricks to have
             many warnings/errors-->
            <b-table
              striped
              hover
              centered
              :items="summary_table_items"
              :fields="summary_table_fields"
            ></b-table>
            <hr />
            <h4 class="text-center m-4">
              <b-badge variant="primary">
                {{ $t('total_errors') }}
                <b-badge pill variant="light">
                  {{ project_problems_total_count }}
                  <span class="sr-only">total warnings and errors</span>
                </b-badge>
              </b-badge>
            </h4>
          </b-col>
          <b-col>
            <simple-dynamic-pie-chart
              :data_array="summary_pie_data"
              :label_array="summary_pie_labels"
              chart_title="error_summary"
            />
          </b-col>
        </b-row>
      </b-card>

      <unroll-element
        :available="!_.isEmpty(project_warnings)"
        :start_opened="false"
        title="Warnings details brick by brick"
      >
        <b-list-group>
          <b-list-group-item
            v-for="info in project_warnings"
            :key="info.brick_id"
          >
            <p class="d-flex justify-content-between align-items-center">
              <span class="h2">
                <b-badge pill variant="primary">
                  Brick
                  <b> {{ bricks_undeleted.bricks[info.brick_id].name }} </b>
                </b-badge>
              </span>
              <b-badge variant="primary" pill>
                {{ info.count }}
                <span class="sr-only">brick total count of problems</span>
              </b-badge>
            </p>
            <hr />
            <b-card-group deck>
              <b-card
                v-if="info.has_runtime_errors"
                border-variant="customdanger"
                header="Runtime Errors"
                header-bg-variant="customdanger"
                header-text-variant="white"
                class="text-center"
              >
                <ul>
                  <li v-for="runtime_error_info in info.runtime_errors">
                    Poid
                    <b
                      ><i>{{ runtime_error_info.poid }}</i></b
                    >
                    with error
                    <b>{{ runtime_error_info.text }}</b>
                  </li>
                </ul>
              </b-card>
              <b-card
                v-if="info.has_semantic_errors"
                border-variant="orange"
                header="Semantic Errors"
                header-bg-variant="orange"
                header-text-variant="white"
              >
                <ul>
                  <li v-for="semantic_error_info in info.semantic_errors">
                    Metavariables
                    <b
                      ><i>{{ semantic_error_info.metavar }}</i></b
                    >
                    with error
                    <b>{{ semantic_error_info.text }}</b>
                  </li>
                </ul>
              </b-card>
              <b-card
                v-if="info.has_runtime_warnings"
                border-variant="primary"
                header="Runtime Warnings"
                header-bg-variant="customwarning"
                header-text-variant="white"
              >
                <ul>
                  <li v-for="runtime_warning_info in info.runtime_warnings">
                    Poid
                    <b
                      ><i>{{ runtime_warning_info.poid }}</i></b
                    >
                    with warning
                    <b>{{ runtime_warning_info.text }}</b>
                  </li>
                </ul>
              </b-card>
              <b-card
                v-if="info.has_semantic_warnings"
                border-variant="info"
                header="Semantic Warnings"
                header-bg-variant="info"
                header-text-variant="white"
              >
                <ul>
                  <li v-for="semantic_warning_info in info.semantic_warnings">
                    <b>{{ semantic_warning_info }}</b>
                  </li>
                </ul>
              </b-card>
            </b-card-group>
          </b-list-group-item>
        </b-list-group>
      </unroll-element>
    </b-container>
  </generic-in-app-screen>
</template>

<style scoped>
.info-box {
  cursor: pointer;
}
.info-box-content {
  text-align: center;
  vertical-align: middle;
  display: inherit;
}
</style>

<i18n>
{
  "en": {
    "current_projects_warnings": "Current projects warnings and errors",
    "total_errors": "Total errors and warnings",
    "runtime_errors": "Runtime Errors",
    "semantic_errors": "Semantic Errors",
    "runtime_warnings": "Runtime Warnings",
    "semantic_warnings": "Semantic Warnings"
  },
  "fr": {
    "current_projects_warnings": "Liste des avertissements et erreurs du projet",
    "total_errors": "Nombre d'avertissements et d'erreurs",
    "runtime_errors": "Erreurs durant l'exécution",
    "semantic_errors": "Erreurs sémantiques",
    "runtime_warnings": "Avertissements durant l'exécution",
    "semantic_warnings": "Avertissements sémantiques"
  }
}
</i18n>

<script>
import _ from 'lodash'

import { mapGetters } from 'vuex'

import { subscription_mixin, project_mixin } from '@/components/mixin.js'
import { extended_dict_forEach, extended_dict_values } from '@/utils.js'

import UnrollElement from './unroll_element.vue'
import SimpleDynamicPieChart from './simple_dynamic_pie_chart.vue'
import GenericInAppScreen from '@/components/generic_in_app_screen.vue'

// *****************************************************************************

export default {
  name: 'ProjectWarnings',
  components: {
    UnrollElement,
    SimpleDynamicPieChart,
    GenericInAppScreen,
  },
  mixins: [subscription_mixin, project_mixin],
  data() {
    return {
      // TODO : Can be dynamic in function of the size of the screen
      bricks_to_show_in_table: 3,
    }
  },
  computed: {
    _() {
      return _
    },
    summary_table_fields() {
      let res = [
        { key: 'brick_name', class: 'text-center' },
        { key: 'count', class: 'text-center' },
      ]
      return res
    },
    sorted_count_problems_bricks() {
      let res = []
      let self = this
      _.forEach(this.project_warnings, function(info) {
        let brick = self.bricks_undeleted.bricks[info.brick_id]
        let info_count = { brick_name: brick.name, count: info.count }
        res.push(info_count)
      })
      // Sort according to count
      res = res.sort((a, b) => b.count - a.count)
      return res
    },
    summary_table_items() {
      let res = this.sorted_count_problems_bricks.slice(
        0,
        this.bricks_to_show_in_table
      )
      return res
    },
    // project_id() {
    //   let route = this.$route.path
    //   let regexp = new RegExp('(\\d)*/warnings')
    //   return Number(route.match(regexp)[0].split('/')[0])
    // },
    // cur_project() {
    //   let res =
    //     (this.automl_projects_undeleted_object.projects || {})[
    //       this.project_id
    //     ] || {}
    //   return res
    // },
    bricks_undeleted() {
      return this.automl_bricks_undeleted_object_per_project(this.project_id)
    },
    project_warnings() {
      let res = []
      let self = this
      _.forEach(this.bricks_undeleted['lists'], function(x) {
        let brick = self.bricks_undeleted.bricks[x]

        // Find runtime  errors
        let runtime_errors = []
        let dict = self.cur_project.failed_poid_to_reason || {}
        let brick_poids = extended_dict_values(brick.last_poids_build_cache)
        extended_dict_forEach(dict, (poid, failure_reason) => {
          if (_.includes(brick_poids, poid)) {
            // N.B PL : Why the falure reason is in a list, can we have more than one ?
            runtime_errors.push({ poid, text: failure_reason[1][0] })
          }
        })
        let has_runtime_errors = !_.isEmpty(runtime_errors)

        // Find semantic errors
        let semantic_errors = []
        dict = (brick || {}).compute_failure_reasons || {}
        extended_dict_forEach(dict, (metavar, failure_reason) => {
          semantic_errors.push({ metavar, text: failure_reason[1] })
        })
        let has_semantic_errors = !_.isEmpty(semantic_errors)

        // Find runtime warnings (in tracking_info.warnings basically)
        const unique_poids = _.uniq(brick_poids)

        const category_and_arguments_list = _.map(unique_poids, poid => [
          'poid_tracking_info',
          poid,
        ])
        const tracking_infos = self.fetch_many(
          'project_warnings',
          category_and_arguments_list
        )
        let runtime_warnings = []
        for (const [poid, tracking_info] of _.zip(
          unique_poids,
          tracking_infos
        )) {
          const current_warnings = _.map(
            (tracking_info || {}).warnings || [],
            _.head
          )
          if (!_.isEmpty(current_warnings)) {
            runtime_warnings.push({ poid, text: current_warnings })
          }
        }

        let has_runtime_warnings = !_.isEmpty(runtime_warnings)

        // Find semantic warnings
        let semantic_warnings = []
        semantic_warnings = brick.semantic_warnings
        let has_semantic_warnings = !_.isEmpty(semantic_warnings)

        // Find if any warnings/errors for this brick
        let has_errors = _.some(
          [
            has_runtime_errors,
            has_semantic_errors,
            has_runtime_warnings,
            has_semantic_warnings,
          ],
          Boolean
        )
        if (has_errors) {
          let count =
            _.size(runtime_errors) +
            _.size(semantic_errors) +
            _.size(runtime_warnings) +
            _.size(semantic_warnings)
          let brick_info = {
            brick_id: x,
            has_runtime_errors,
            has_semantic_errors,
            has_runtime_warnings,
            has_semantic_warnings,
            runtime_errors,
            semantic_errors,
            runtime_warnings,
            semantic_warnings,
            count,
          }
          res.push(brick_info)
        }
      })
      return res
    }, // project_warnings() {
    //   let res = []
    //   let self = this
    //   _.forEach(this.bricks_undeleted['lists'], function(x) {
    //     let brick = self.bricks_undeleted.bricks[x]

    //     // Find runtime  errors
    //     let runtime_errors = []
    //     let dict = self.cur_project.failed_poid_to_reason || {}
    //     let brick_poids = extended_dict_values(brick.last_poids_build_cache)
    //     extended_dict_forEach(dict, (poid, failure_reason) => {
    //       if (_.includes(brick_poids, poid)) {
    //         // N.B PL : Why the falure reason is in a list, can we have more than one ?
    //         runtime_errors.push({ poid, text: failure_reason[1][0] })
    //       }
    //     })
    //     let has_runtime_errors = !_.isEmpty(runtime_errors)

    //     // Find semantic errors
    //     let semantic_errors = []
    //     dict = (brick || {}).compute_failure_reasons || {}
    //     extended_dict_forEach(dict, (metavar, failure_reason) => {
    //       semantic_errors.push({ metavar, text: failure_reason[1] })
    //     })
    //     let has_semantic_errors = !_.isEmpty(semantic_errors)

    //     // Find runtime warnings (in tracking_info.warnings basically)
    //     let runtime_warnings = []
    //     let unique_poids = _.uniq(brick_poids)
    //     _.forEach(unique_poids || {}, function(poid) {
    //       let tracking_info_token = _.uniqueId('tracking_info_token' + poid)
    //       let tracking_info =
    //         self.fetch_poid_tracking_info(tracking_info_token, poid) || {}

    //       // Warnings are tuples (error_code, context_dict)
    //       // We just want to get the error_codes without
    //       // the context_dict (for now)
    //       let current_warnings = _.map(tracking_info.warnings || [], _.head)

    //       if (!_.isEmpty(current_warnings)) {
    //         runtime_warnings.push({ poid, text: current_warnings })
    //       }
    //     })
    //     let has_runtime_warnings = !_.isEmpty(runtime_warnings)

    //     // Find semantic warnings
    //     let semantic_warnings = []
    //     semantic_warnings = brick.semantic_warnings
    //     let has_semantic_warnings = !_.isEmpty(semantic_warnings)

    //     // Find if any warnings/errors for this brick
    //     let has_errors = _.some(
    //       [
    //         has_runtime_errors,
    //         has_semantic_errors,
    //         has_runtime_warnings,
    //         has_semantic_warnings,
    //       ],
    //       Boolean
    //     )
    //     if (has_errors) {
    //       let count =
    //         _.size(runtime_errors) +
    //         _.size(semantic_errors) +
    //         _.size(runtime_warnings) +
    //         _.size(semantic_warnings)
    //       let brick_info = {
    //         brick_id: x,
    //         has_runtime_errors,
    //         has_semantic_errors,
    //         has_runtime_warnings,
    //         has_semantic_warnings,
    //         runtime_errors,
    //         semantic_errors,
    //         runtime_warnings,
    //         semantic_warnings,
    //         count,
    //       }
    //       res.push(brick_info)
    //     }
    //   })
    //   return res
    // },
    summary_warnings() {
      let res = {
        runtime_errors_count: 0,
        semantic_errors_count: 0,
        runtime_warnings_count: 0,
        semantic_warnings_count: 0,
      }
      _.forEach(this.project_warnings, function(x) {
        res.runtime_errors_count += _.size(x.runtime_errors)
        res.semantic_errors_count += _.size(x.semantic_errors)
        res.runtime_warnings_count += _.size(x.runtime_warnings)
        res.semantic_warnings_count += _.size(x.semantic_warnings)
      })
      return res
    },
    // N.B problems is warnings + errors
    project_problems_total_count() {
      let summary_warnings = this.summary_warnings
      let res =
        summary_warnings.runtime_errors_count +
        summary_warnings.semantic_errors_count +
        summary_warnings.runtime_warnings_count +
        summary_warnings.semantic_warnings_count
      return res
    },
    summary_pie_data() {
      let summary_warnings = this.summary_warnings
      let res = [
        summary_warnings.runtime_errors_count,
        summary_warnings.semantic_errors_count,
        summary_warnings.runtime_warnings_count,
        summary_warnings.semantic_warnings_count,
      ]
      return res
    },
    summary_pie_labels() {
      let res = [
        this.$t('runtime_errors'),
        this.$t('semantic_errors'),
        this.$t('runtime_warnings'),
        this.$t('semantic_warnings'),
      ]
      return res
    },
    ...mapGetters([
      'automl_projects_undeleted_object',
      'automl_bricks_undeleted_object_per_project',
    ]),
  },
  beforeMount() {
    this.fetch_collections(['projects', 'bricks', 'users', 'project_rights'])
  },
  methods: {},
}
</script>
